import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { Question } from '../../models/RoboCall/Question';
import { Questionnaire } from '../../models/RoboCall/Questionnaire';
import { Status } from '../../models/common/Status';
import { RootState } from '../store';
import { apiClientWithApi } from '../../utils/http';

export interface QuestionnairesState {
  questionnaires: Questionnaire[];
  status: Status;
}

const initialState: QuestionnairesState = {
  questionnaires: [],
  status: {
    responseLoading: true,
    responseError: null,
    requestSuccess: null,
    requestError: null,
  },
};

export const getQuestionnaires = createAsyncThunk(
  'questionnaire/questionnaire',
  async (_, { rejectWithValue }) => {
    try {
      const response =
        await apiClientWithApi.get<Questionnaire[]>('api/questionnaire');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      }
    }
    return rejectWithValue('Серверная ошибка!');
  },
);

export const saveQuestionnaire = createAsyncThunk(
  'questionnaire/saveQuestionnaire',
  async (questionnaire: Questionnaire, { rejectWithValue }) => {
    try {
      const response = await apiClientWithApi.post<Questionnaire[]>(
        'questionnaire',
        questionnaire,
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response!.data);
      } else return rejectWithValue('Не удалось сохранить анкету!');
    }
  },
);

export const deleteQuestionnaire = createAsyncThunk(
  'questionnaire/deleteQuestionnaire',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await apiClientWithApi.delete(`questionnaire/${id}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Серверная ошибка!');
    }
  },
);

export const questionnaireSlice = createSlice({
  name: 'questionnaire',
  initialState,
  reducers: {
    addQuestionnaire: (state) => {
      state.questionnaires.push({
        name: 'Новая анкета',
        questions: [{}] as Question[],
      } as Questionnaire);

      return state;
    },
    updateRequestStatus: (state) => {
      state.status.requestSuccess = null;
      state.status.requestError = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getQuestionnaires.pending, (state) => {
        state.status.responseLoading = true;
        state.status.responseError = null;
      })
      .addCase(getQuestionnaires.fulfilled, (state, action) => {
        state.status.responseLoading = false;
        state.status.responseError = null;
        if (action.payload.length > 0) {
          state.questionnaires = action.payload;
        }
      })
      .addCase(deleteQuestionnaire.fulfilled, (state, action) => {
        state.status.requestSuccess = 'Анкета успешно удалена';
        state.questionnaires = state.questionnaires.filter(
          (x) => x.id !== action.payload,
        );
      })
      .addCase(getQuestionnaires.rejected, (state) => {
        state.status.responseLoading = false;
        state.status.responseError = 'Не удалось загрузить анкеты';
      })
      .addCase(saveQuestionnaire.fulfilled, (state, action) => {
        state.status.requestSuccess = 'Анкета успешно сохранена';
        state.questionnaires = action.payload;
      })
      .addCase(saveQuestionnaire.rejected, (state) => {
        state.status.requestError = 'Не удалось сохранить анкету';
      });
  },
});

export default questionnaireSlice.reducer;

export const { addQuestionnaire, updateRequestStatus } =
  questionnaireSlice.actions;

export const selectQuestionnaires = (state: RootState) =>
  state.questionnaires.questionnaires;

export const questionnairesStatus = (state: RootState) =>
  state.questionnaires.status;
